import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import "./MaterialViewVisibilityTable.css";

const INITIAL_VISIBLE_ROWS = 50;
const ROWS_PER_BATCH = 50;
const LOAD_MORE_OFFSET = 120;

const SHIMMER_ROW_KEYS = [
  "shimmer-row-1",
  "shimmer-row-2",
  "shimmer-row-3",
  "shimmer-row-4",
  "shimmer-row-5",
  "shimmer-row-6",
];

const SHIMMER_COLUMN_KEYS = [
  "shimmer-col-1",
  "shimmer-col-2",
  "shimmer-col-3",
  "shimmer-col-4",
  "shimmer-col-5",
  "shimmer-col-6",
];

const MaterialViewVisibilityTable = ({ data = [], loading = false }) => {
  const tableWrapperRef = useRef(null);

  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);

  const formatToCappedUnit = useCallback((value) => {
    if (value === null || value === undefined) {
      return "-";
    }

    if (value < 1000) {
      return `${value}`;
    }

    if (value < 100000) {
      return `${(value / 1000).toFixed(1)} K`;
    }

    if (value < 10000000) {
      return `${(value / 100000).toFixed(1)} L`;
    }

    return `${(value / 10000000).toFixed(1)} Cr`;
  }, []);

  const formatNumber = useCallback((value) => {
    if (value === null || value === undefined) {
      return "-";
    }

    return value.toLocaleString();
  }, []);

  const formatValue = useCallback(
    (value) => {
      if (value === null || value === undefined) {
        return "-";
      }

      if (typeof value === "number") {
        if (value >= 10000) {
          return formatToCappedUnit(value);
        }

        return formatNumber(value);
      }

      return value;
    },
    [formatNumber, formatToCappedUnit],
  );

  const columns = useMemo(() => {
    if (loading || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const firstRow = data[0];

    if (!firstRow || typeof firstRow !== "object") {
      return [];
    }

    return Object.keys(firstRow).filter((key) => key !== "is_total");
  }, [data, loading]);

  const firstColumn = columns[0] || "";

  const visibleData = useMemo(() => {
    return data.slice(0, visibleRowCount);
  }, [data, visibleRowCount]);

  const hasMoreRows = visibleRowCount < data.length;

  useEffect(() => {
    setVisibleRowCount(Math.min(INITIAL_VISIBLE_ROWS, data.length));

    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTop = 0;
      tableWrapperRef.current.scrollLeft = 0;
    }
  }, [data]);

  const loadMoreRows = useCallback(() => {
    setVisibleRowCount((previousCount) =>
      Math.min(previousCount + ROWS_PER_BATCH, data.length),
    );
  }, [data.length]);

  const handleTableScroll = useCallback(
    (event) => {
      if (loading || !hasMoreRows) {
        return;
      }

      const scrollContainer = event.currentTarget;

      const distanceFromBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;

      if (distanceFromBottom <= LOAD_MORE_OFFSET) {
        loadMoreRows();
      }
    },
    [hasMoreRows, loadMoreRows, loading],
  );

  const shimmerColumnCount = columns.length || 6;

  const visibleShimmerColumns = SHIMMER_COLUMN_KEYS.slice(
    0,
    shimmerColumnCount,
  );

  const renderLoadingRows = () => {
    return SHIMMER_ROW_KEYS.map((rowKey) => (
      <tr key={rowKey}>
        {visibleShimmerColumns.map((columnKey) => (
          <td key={`${rowKey}-${columnKey}`}>
            <div className="shimmerCell" />
          </td>
        ))}
      </tr>
    ));
  };

  const renderTableBody = () => {
    if (loading) {
      return renderLoadingRows();
    }

    if (!Array.isArray(data) || data.length === 0) {
      return [
        <tr key="no-data">
          <td colSpan={Math.max(columns.length, 1)}>No data available</td>
        </tr>,
      ];
    }

    return visibleData.map((row, rowIndex) => {
      const firstColumnValue = row?.[firstColumn];
      const totalMarker = row?.is_total === 1 ? "total" : "data";

      const rowKey = `${totalMarker}-${firstColumnValue ?? "row"}-${rowIndex}`;

      return (
        <tr
          key={rowKey}
          className={row?.is_total === 1 ? "summaryTotalRow" : ""}
        >
          {columns.map((column) => {
            const value = row?.[column];

            return (
              <td
                key={`${rowKey}-${column}`}
                className={column === firstColumn ? "stickyColumn" : ""}
                title={
                  value !== null && value !== undefined
                    ? String(value)
                    : undefined
                }
              >
                {formatValue(value)}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  const renderTableHeaderCells = () => {
    if (loading) {
      return SHIMMER_COLUMN_KEYS.map((columnKey) => (
        <th key={`header-${columnKey}`}>
          <div className="shimmerCell" style={{ height: "16px" }} />
        </th>
      ));
    }

    return columns.map((column) => (
      <th key={column} className={column === firstColumn ? "stickyColumn" : ""}>
        {column}
      </th>
    ));
  };

  return (
    <div
      ref={tableWrapperRef}
      className="tableWrapper"
      onScroll={handleTableScroll}
    >
      <table className="visibilityTable">
        <thead>
          <tr>{renderTableHeaderCells()}</tr>
        </thead>

        <tbody>{renderTableBody()}</tbody>
      </table>

      {!loading && hasMoreRows && (
        <div className="summaryLoadMoreIndicator">Scroll to load more</div>
      )}
    </div>
  );
};

MaterialViewVisibilityTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
};

export default React.memo(MaterialViewVisibilityTable);
